import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Star, TrendingUp, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { ContactModal } from '@/components/modals/ContactModal';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { useMarketingImages } from '@/hooks/useMarketingImages';
import { HeroSplitInfographic } from './HeroSplitInfographic';

export default function LandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    getNavigationText,
    getLandingBenefits,
    getLandingFeatures,
    getLandingCTA,
    getLandingFooter,
    getBenefitsFeatures,
    getKeyFeaturesList,
  } = useMarketingTranslations();
  const { getImage } = useMarketingImages();

  // Clear any stale authentication state when visiting landing page

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'KernLogic',
    description: 'Enterprise-Grade PIM for Mid-Manufacturers & Retailers',
    url: 'https://kernlogic.com',
    logo: 'https://kernlogic.com/images/logo.webp',
    sameAs: [
      'https://twitter.com/kernlogic',
      'https://linkedin.com/company/kernlogic',
      'https://github.com/kernlogic',
    ],
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://kernlogic.com',
      },
    ],
  };

  const handleRequestWalkthrough = () => {
    setIsModalOpen(true);
  };

  // Get structured data from translations
  const benefitsFeatures = getBenefitsFeatures();
  const keyFeatures = getKeyFeaturesList();

  // Map key features to marketing image features
  const getFeatureImage = (idx: number): string => {
    const featureMap = ['dataImport', 'attributeFamily', 'analytics'];
    return getImage(featureMap[idx] || 'analytics');
  };

  return (
    <main
      role="main"
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-scroll-container
    >
      {/* SEO Meta Tags */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>

      {/* Navigation */}
      <motion.header
        className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm"
        role="banner"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <nav
          className="container mx-auto px-6 py-4"
          role="navigation"
          aria-label={getNavigationText('ariaLabel')}
        >
          <div className="flex items-center justify-between">
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Link to="/marketing" className="group flex items-center">
                <motion.img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getNavigationText('brand')}
                  className="size-12 transition-transform group-hover:scale-105"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="ml-3 text-xl font-bold leading-[1.2] text-white transition-colors group-hover:text-primary-300">
                  {getNavigationText('brand')}
                </span>
              </Link>
            </motion.div>
            <div className="flex items-center space-x-6">
              <motion.div
                className="hidden items-center space-x-6 md:flex"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/product"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                    aria-current="page"
                  >
                    {getNavigationText('product')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/pricing"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('pricing')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/login"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('login')}
                  </Link>
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <LanguageToggle />
              </motion.div>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Hero Section */}
      <HeroSplitInfographic />

      {/* Key Benefits Section */}
      <section
        className="relative overflow-hidden bg-slate-800/50 py-24"
        aria-labelledby="benefits-title"
      >
        {/* Dynamic Background Elements */}
        <motion.div
          className="absolute left-0 top-0 size-96 rounded-full bg-primary-500/5 blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, 80, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 size-80 rounded-full bg-primary-600/5 blur-2xl"
          animate={{
            x: [0, -120, 0],
            y: [0, -60, 0],
            scale: [1, 1.4, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 5,
          }}
        />

        <div className="container relative z-10 mx-auto px-6">
          <div className="mb-20 text-center">
            <motion.h2
              id="benefits-title"
              className="mb-6 py-1 text-4xl font-bold leading-[1.2] text-white md:text-5xl"
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, type: 'spring', bounce: 0.4 }}
            >
              {getLandingBenefits('title')}
            </motion.h2>
            <motion.p
              className="mx-auto max-w-3xl text-xl leading-relaxed text-slate-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {getLandingBenefits('subtitle')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {benefitsFeatures.map((feature, idx) => (
              <motion.article
                key={idx}
                className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 shadow-xl backdrop-blur-sm transition-all duration-500 hover:border-primary-500/40 hover:shadow-2xl hover:shadow-primary-500/20"
                initial={{ opacity: 0, y: 60, scale: 0.8 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.2,
                  type: 'spring',
                  bounce: 0.4,
                }}
                whileHover={{
                  scale: 1.05,
                  y: -10,
                  transition: { duration: 0.3 },
                }}
              >
                {/* Card Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-primary-600/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  initial={false}
                />

                <motion.div
                  className="relative z-10 mb-6 inline-flex size-16 items-center justify-center rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-500/20 to-primary-600/20"
                  whileHover={{
                    scale: 1.2,
                    rotate: 360,
                    transition: { duration: 0.5 },
                  }}
                >
                  <motion.div
                    animate={{
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: idx * 0.5,
                    }}
                  >
                    {idx === 0 && <Zap className="size-8 text-primary-400" aria-hidden="true" />}
                    {idx === 1 && (
                      <CheckCircle2 className="size-8 text-primary-400" aria-hidden="true" />
                    )}
                    {idx === 2 && (
                      <TrendingUp className="size-8 text-primary-400" aria-hidden="true" />
                    )}
                  </motion.div>
                </motion.div>

                <motion.h3
                  className="relative z-10 mb-4 text-2xl font-semibold leading-[1.3] text-white"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.2 + 0.3 }}
                >
                  {feature.title}
                </motion.h3>

                <motion.p
                  className="relative z-10 mb-6 leading-relaxed text-slate-300"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.2 + 0.4 }}
                >
                  {feature.description}
                </motion.p>

                <motion.ul
                  className="relative z-10 space-y-3"
                  role="list"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.2 + 0.5 }}
                >
                  {feature.bullets.map((bullet, i) => (
                    <motion.li
                      key={i}
                      className="group/item flex cursor-pointer items-center"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.5,
                        delay: idx * 0.2 + 0.6 + i * 0.1,
                      }}
                      whileHover={{ x: 5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.3, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <CheckCircle2
                          className="mr-3 size-5 text-green-400 transition-colors group-hover/item:text-green-300"
                          aria-hidden="true"
                        />
                      </motion.div>
                      <span className="text-slate-300 transition-colors group-hover/item:text-white">
                        {bullet}
                      </span>
                    </motion.li>
                  ))}
                </motion.ul>

                {/* Animated Corner Accent */}
                <motion.div
                  className="absolute right-0 top-0 size-20 bg-gradient-to-bl from-primary-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  initial={false}
                />
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Top Features Preview */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 py-24"
        aria-labelledby="features-title"
      >
        {/* Animated Background Grid */}
        <motion.div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}
          animate={{
            backgroundPosition: ['0px 0px', '50px 50px', '0px 0px'],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Floating Orbs */}
        <motion.div
          className="absolute left-1/4 top-1/4 size-72 rounded-full bg-primary-500/10 blur-3xl"
          animate={{
            x: [0, 50, -30, 0],
            y: [0, -40, 60, 0],
            scale: [1, 1.2, 0.8, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <div className="container relative z-10 mx-auto px-6">
          <div className="mb-20 text-center">
            <motion.h2
              id="features-title"
              className="mb-6 py-1 text-4xl font-bold leading-[1.2] text-white md:text-5xl"
              initial={{ opacity: 0, y: 50, rotateX: 45 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 1, type: 'spring', bounce: 0.3 }}
            >
              {getLandingFeatures('title')}
            </motion.h2>
            <motion.p
              className="mx-auto max-w-3xl text-xl leading-relaxed text-slate-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {getLandingFeatures('subtitle')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {keyFeatures.map((feature, idx) => (
              <motion.article
                key={idx}
                className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 shadow-xl backdrop-blur-sm transition-all duration-500 hover:border-primary-500/40 hover:shadow-2xl hover:shadow-primary-500/20"
                initial={{ opacity: 0, y: 80, rotateY: 15 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.2,
                  type: 'spring',
                  bounce: 0.4,
                }}
                whileHover={{
                  scale: 1.03,
                  y: -15,
                  rotateY: 5,
                  transition: { duration: 0.4 },
                }}
              >
                {/* Card Background Glow */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-primary-600/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  initial={false}
                />

                <div className="relative overflow-hidden">
                  <motion.img
                    src={getFeatureImage(idx)}
                    alt={feature.alt}
                    loading="lazy"
                    className="h-56 w-full object-cover transition-all duration-500 group-hover:scale-110"
                    whileHover={{
                      scale: 1.15,
                      filter: 'brightness(1.1) contrast(1.1)',
                    }}
                  />

                  {/* Image Overlay Effects */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent"
                    initial={{ opacity: 0.7 }}
                    whileHover={{ opacity: 0.9 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Floating Feature Badge */}
                  <motion.div
                    className="absolute right-4 top-4 rounded-full bg-primary-500/90 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm"
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.2 + 0.5 }}
                    whileHover={{
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      transition: { duration: 0.5 },
                    }}
                  >
                    {idx === 0 ? 'Import' : idx === 1 ? 'Organize' : 'Analyze'}
                  </motion.div>
                </div>

                <div className="relative z-10 p-8">
                  <motion.h3
                    className="mb-4 text-2xl font-semibold leading-[1.3] text-white transition-colors group-hover:text-primary-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.2 + 0.4 }}
                  >
                    {feature.title}
                  </motion.h3>

                  <motion.p
                    className="leading-relaxed text-slate-300 transition-colors group-hover:text-slate-200"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.2 + 0.5 }}
                  >
                    {feature.description}
                  </motion.p>

                  {/* Interactive Action Button */}
                  <motion.div
                    className="mt-6"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.2 + 0.6 }}
                  >
                    <Link
                      to="/marketing/product"
                      className="group/btn inline-flex items-center text-sm font-medium text-primary-400 transition-colors hover:text-primary-300"
                    >
                      Learn More
                      <motion.div
                        className="ml-2 inline-block"
                        animate={{ x: [0, 5, 0] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <ArrowRight className="size-4" />
                      </motion.div>
                    </Link>
                  </motion.div>
                </div>

                {/* Animated Corner Decoration */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-primary-500 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  initial={false}
                  layoutId={`feature-line-${idx}`}
                />
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section
        className="relative overflow-hidden bg-gradient-to-r from-primary-600 via-primary-500 to-purple-600 py-32"
        aria-labelledby="cta-title"
      >
        {/* Dynamic Background Pattern */}
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='7' cy='7' r='3'/%3E%3Ccircle cx='53' cy='53' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          animate={{
            backgroundPosition: ['0px 0px', '60px 60px', '0px 0px'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Floating Elements */}
        <motion.div
          className="absolute left-16 top-16 size-32 rounded-full bg-white/10 blur-2xl"
          animate={{
            x: [0, 60, -30, 0],
            y: [0, -40, 20, 0],
            scale: [1, 1.3, 0.8, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <motion.div
          className="absolute bottom-16 right-16 size-24 rounded-full bg-white/10 blur-xl"
          animate={{
            x: [0, -50, 40, 0],
            y: [0, 30, -20, 0],
            scale: [1, 0.7, 1.4, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 5,
          }}
        />

        {/* Central Starburst Effect */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-32 w-1 bg-gradient-to-t from-transparent via-white/20 to-transparent"
              style={{
                transformOrigin: 'center bottom',
                transform: `rotate(${i * 45}deg)`,
              }}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 1,
                delay: i * 0.1 + 0.5,
                type: 'spring',
                bounce: 0.6,
              }}
            >
              <motion.div
                className="size-full bg-gradient-to-t from-transparent via-white/20 to-transparent"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                }}
              />
            </motion.div>
          ))}
        </motion.div>

        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-5xl text-center">
            <motion.h2
              id="cta-title"
              className="mb-8 py-2 text-5xl font-bold leading-[1.1] text-white md:text-7xl"
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{
                duration: 1.2,
                type: 'spring',
                bounce: 0.4,
                delay: 0.2,
              }}
            >
              <motion.span
                animate={{
                  textShadow: [
                    '0 0 0px rgba(255, 255, 255, 0)',
                    '0 0 20px rgba(255, 255, 255, 0.5)',
                    '0 0 0px rgba(255, 255, 255, 0)',
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
              >
                {getLandingCTA('title')}
              </motion.span>
            </motion.h2>

            <motion.p
              className="mx-auto mb-12 max-w-3xl text-2xl leading-relaxed text-primary-50"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {getLandingCTA('subtitle')}
            </motion.p>

            {/* Enhanced CTA Button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{
                duration: 0.8,
                delay: 0.8,
                type: 'spring',
                bounce: 0.6,
              }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative inline-block"
              >
                {/* Button Glow Effect */}
                <motion.div
                  className="absolute inset-0 rounded-2xl bg-white/30 blur-2xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />

                <Button
                  asChild
                  size="lg"
                  className="relative h-auto rounded-2xl border-2 border-slate-700 bg-slate-900 px-12 py-6 text-xl font-bold text-white shadow-2xl transition-all duration-300 hover:border-slate-600 hover:bg-slate-800 focus:ring-4 focus:ring-white/50 focus:ring-offset-4 focus:ring-offset-primary-600"
                >
                  <motion.button
                    onClick={handleRequestWalkthrough}
                    whileHover={{
                      boxShadow: '0 0 40px rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <motion.span className="flex items-center">
                      {getLandingCTA('button')}
                      <motion.div
                        className="ml-4"
                        animate={{ x: [0, 8, 0] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <ArrowRight className="size-6 text-white" aria-hidden="true" />
                      </motion.div>
                    </motion.span>
                  </motion.button>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <motion.footer
        className="relative overflow-hidden border-t border-slate-700/50 bg-slate-900 py-16"
        role="contentinfo"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 1 }}
      >
        {/* Animated Background Elements */}
        <motion.div
          className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-primary-500/50 to-transparent"
          animate={{
            scaleX: [0, 1, 0],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Floating Dots */}
        <motion.div className="pointer-events-none absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute size-1 rounded-full bg-primary-400/30"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 3) * 20}%`,
              }}
              animate={{
                y: [0, -20, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 4 + i,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.8,
              }}
            />
          ))}
        </motion.div>

        <div className="container relative z-10 mx-auto px-6">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h4
                className="mb-6 text-xl font-semibold text-white"
                whileHover={{
                  color: '#3b82f6',
                  textShadow: '0 0 8px rgba(59, 130, 246, 0.5)',
                }}
                transition={{ duration: 0.3 }}
              >
                {getLandingFooter('companyInfo')}
              </motion.h4>
              <motion.p
                className="mb-6 text-slate-400"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {getLandingFooter('copyright', {
                  year: new Date().getFullYear(),
                })}
              </motion.p>

              {/* Animated Logo */}
              <motion.div
                className="mt-8 flex items-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <motion.img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt="KernLogic"
                  className="size-8"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
                <motion.span
                  className="text-lg font-bold text-white"
                  animate={{
                    textShadow: [
                      '0 0 0px rgba(59, 130, 246, 0)',
                      '0 0 10px rgba(59, 130, 246, 0.3)',
                      '0 0 0px rgba(59, 130, 246, 0)',
                    ],
                  }}
                  transition={{ duration: 6, repeat: Infinity, delay: 2 }}
                >
                  KernLogic
                </motion.span>
              </motion.div>
            </motion.div>

            {/* Empty middle column with decorative element */}
            <motion.div
              className="hidden items-center justify-center md:flex"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.div
                className="h-px w-32 bg-gradient-to-r from-transparent via-primary-400/50 to-transparent"
                animate={{
                  scaleX: [0.5, 1, 0.5],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </motion.div>

            {/* Links */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.h4
                className="mb-6 text-xl font-semibold text-white"
                whileHover={{
                  color: '#3b82f6',
                  textShadow: '0 0 8px rgba(59, 130, 246, 0.5)',
                }}
                transition={{ duration: 0.3 }}
              >
                {getLandingFooter('quickLinks')}
              </motion.h4>
              <motion.ul
                className="space-y-3 text-slate-400"
                role="list"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                {[
                  { to: '/marketing', label: getLandingFooter('links.home') },
                  {
                    to: '/marketing/product',
                    label: getLandingFooter('links.features'),
                  },
                  {
                    to: '/marketing/pricing',
                    label: getLandingFooter('links.pricing'),
                  },
                  { to: '/login', label: getLandingFooter('links.login') },
                ].map((link, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.9 + i * 0.1 }}
                  >
                    <motion.div
                      whileHover={{ x: 8, scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={link.to}
                        className="group relative inline-block rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                      >
                        {link.label}
                        <motion.div
                          className="absolute inset-x-2 -bottom-1 h-0.5 origin-left bg-primary-400"
                          initial={{ scaleX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </Link>
                    </motion.div>
                  </motion.li>
                ))}
              </motion.ul>

              {/* Social Proof Indicators */}
              <motion.div
                className="mt-8 flex items-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <motion.div
                  className="flex items-center gap-2 text-sm text-slate-400"
                  whileHover={{ scale: 1.05, color: '#3b82f6' }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  >
                    <Star className="size-4 fill-current text-yellow-400" />
                  </motion.div>
                  <span>Enterprise Ready</span>
                </motion.div>

                <motion.div
                  className="flex items-center gap-2 text-sm text-slate-400"
                  whileHover={{ scale: 1.05, color: '#10b981' }}
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <CheckCircle2 className="size-4 text-green-400" />
                  </motion.div>
                  <span>Secure & Compliant</span>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Legal Links */}
          <motion.div
            className="mt-12 border-t border-slate-700/50 pt-8 text-center text-slate-500"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 1.4 }}
          >
            <motion.div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { to: '/privacy', label: getLandingFooter('legal.privacy') },
                { to: '/terms', label: getLandingFooter('legal.terms') },
                {
                  to: '/cookies',
                  label: getLandingFooter('legal.cookies'),
                },
              ].map((link, i) => (
                <motion.span key={i} className="flex items-center">
                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to={link.to}
                      className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                  {i < 2 && (
                    <motion.span
                      className="mx-2 text-slate-600"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i }}
                    >
                      ·
                    </motion.span>
                  )}
                </motion.span>
              ))}
            </motion.div>

            {/* Final Touch */}
            <motion.p
              className="mt-4 text-xs"
              animate={{
                opacity: [0.7, 1, 0.7],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            ></motion.p>
          </motion.div>
        </div>
      </motion.footer>

      {/* Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
}
